'use client';

import { useState, useEffect } from 'react';
import { chartRegistry } from '@/config/chart-registry';

export interface UseChartSelectionReturn {
  selectedCharts: Set<number>;
  toggleChart: (chartId: number) => void;
  isChartSelected: (chartId: number) => boolean;
  selectAll: () => void;
  deselectAll: () => void;
  applyPreset: (chartIds: number[]) => void;
  exportSelection: () => number[];
  loadFromUrl: () => void;
}

export function useChartSelection(): UseChartSelectionReturn {
  // Initialize selected charts from localStorage, URL, or defaultVisible
  const [selectedCharts, setSelectedCharts] = useState<Set<number>>(() => {
    if (typeof window !== 'undefined') {
      // First, try to load from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const datasets = urlParams.get('d');
      if (datasets !== null) {
          const dss = datasets.split(',').map(parseInt);
          const charts = chartRegistry.charts.filter(chart => dss.includes(chart.queryId || 0))
          return new Set(charts.map(c => c.id))
      }
      const chartsParam = urlParams.get('c');
      if (chartsParam !== null) {
        try {
          const csv = atob(chartsParam);
          if (csv !== '') {
            const ids = csv.split(',').map(id => parseInt(id, 10));
            // Filter out invalid IDs
            const validIds = ids.filter(id =>
              chartRegistry.charts.some(chart => chart.id === id)
            );
            return new Set(validIds);
          }
        } catch {
          // Invalid base64, fall through to localStorage
        }
      }
    }
    // Default to all charts with defaultVisible: true
    return new Set(
      chartRegistry.charts
        .filter(chart => chart.defaultVisible)
        .map(chart => chart.id)
    );
  });

  // Save to localStorage when selection changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedCharts', JSON.stringify(Array.from(selectedCharts)));
    }
  }, [selectedCharts]);

  const toggleChart = (chartId: number) => {
    setSelectedCharts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chartId)) {
        newSet.delete(chartId);
      } else {
        newSet.add(chartId);
      }
      return newSet;
    });
  };

  const isChartSelected = (chartId: number) => {
    return selectedCharts.has(chartId);
  };

  const selectAll = () => {
    setSelectedCharts(new Set(chartRegistry.charts.map(chart => chart.id)));
  };

  const deselectAll = () => {
    setSelectedCharts(new Set());
  };

  const applyPreset = (chartIds: number[]) => {
    setSelectedCharts(new Set(chartIds));
  };

  const exportSelection = (): number[] => {
    return Array.from(selectedCharts);
  };

  const loadFromUrl = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const chartsParam = urlParams.get('c');
      if (chartsParam !== null) {
        try {
          const csv = atob(chartsParam);
          if (csv !== '') {
            const ids = csv.split(',').map(id => parseInt(id, 10));
            const validIds = ids.filter(id =>
              chartRegistry.charts.some(chart => chart.id === id)
            );
            setSelectedCharts(new Set(validIds));
          } else {
            setSelectedCharts(new Set());
          }
        } catch {
          // Invalid base64, do nothing
        }
      }
    }
  };

  return {
    selectedCharts,
    toggleChart,
    isChartSelected,
    selectAll,
    deselectAll,
    applyPreset,
    exportSelection,
    loadFromUrl
  };
}
